'use client';
import Spinner from '@/src/components/ui/Spinner';
import useNavigation from '@/src/lib/hooks/useNavigation';
import React, { FC, useState, useTransition } from 'react';
import { motion } from 'framer-motion';
import { DigitalCardCategoriesResponse } from '../services/DigitalCardsServices';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';

type Props = {
  categories: DigitalCardCategoriesResponse[];
};

const DigitalCardCategories: FC<Props> = ({ categories }) => {
  const { createQueryString, removeQueryString } = useNavigation();
  const [isPending, startTransition] = useTransition();
  const [clickedCategoryId, setClickedCategoryId] = useState<string | null>(null);

  const params = useSearchParams();
  const categoryParams = params.get('category');
  const locale = useLocale();
  const isArabic = locale === 'ar';

  const handleCategoryClick = (categoryId: string, categoryName: string) => {
    setClickedCategoryId(categoryId);
    startTransition(() => {
      if (categoryId === categoryParams) {
        // If clicking the same category, remove it (toggle off)
        removeQueryString('category');
        removeQueryString('cn');
      } else {
        // If clicking a different category, select it (toggle on)
        createQueryString({
          category: categoryId,
          cn: encodeURIComponent(categoryName),
        });
      }
    });
  };

  return (
    <div className="styled-scroll flex flex-nowrap gap-4 overflow-x-auto py-4">
      {categories?.map((item) => {
        const isActive = item.id.toString() === categoryParams;
        const isLoading = isPending && item.id.toString() === clickedCategoryId;

        return (
          <motion.button
            key={item.id}
            onClick={() => handleCategoryClick(item.id.toString(), item.englishName.toString())}
            initial={{ scale: 1, backgroundColor: '#fff', color: '#000' }}
            animate={{
              backgroundColor: isActive ? '#000' : '#fff',
              color: isActive ? '#fff' : '#000',
            }}
            transition={{ duration: 0.3 }}
            className="border-secondary flex max-w-fit shrink-0 cursor-pointer gap-1 overflow-hidden rounded-2xl border-2 px-8 py-3 text-sm font-medium"
          >
            {isArabic ? item.arabicName : item.englishName} {isLoading && <Spinner size="small" />}
          </motion.button>
        );
      })}
    </div>
  );
};

export default DigitalCardCategories;
